import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchTemplates, fetchTemplateMessage } from './attendanceApi';
import { TAP, classLabel } from './ClassAccordion';
import { copyText } from '../../utils/copyText';

const storeKey = (date) => `waDispatch:${date}`;
const loadSent = (date) => { try { return JSON.parse(localStorage.getItem(storeKey(date))) || {}; } catch (e) { return {}; } };

// Post-save queue. Template is chosen ONCE at the top; messages are PRE-FETCHED per class so that
// tapping [Send Report] opens WhatsApp synchronously inside the click (popup blockers/PWAs require it).
// isSent is persisted in localStorage (per date) so it survives the app switch to WhatsApp and back.
export default function WhatsAppDispatcher({ user, date, classIds, onBack, onClose }) {
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [msgs, setMsgs] = useState({});      // { [classId]: { message, groupLink } | { error } }
  const [sent, setSent] = useState(() => loadSent(date));
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchTemplates(user)
      .then((r) => {
        setTemplates(r.templates);
        const last = localStorage.getItem('waDispatchTemplate');
        const pick = r.templates.find((t) => t.id === last) || r.templates[0];
        if (pick) setTemplateId(pick.id);
      })
      .catch((e) => { setTemplates([]); setNote(e.response?.data?.error || 'Could not load templates'); });
  }, [user]);

  // Build every class message once per template change.
  useEffect(() => {
    if (!templateId) return undefined;
    let cancelled = false;
    localStorage.setItem('waDispatchTemplate', templateId);
    setMsgs({});
    classIds.forEach(async (c) => {
      try {
        const r = await fetchTemplateMessage(user, c, date, templateId);
        if (!cancelled) setMsgs((m) => ({ ...m, [c]: { message: r.message, groupLink: r.groupLink } }));
      } catch (e) {
        if (!cancelled) setMsgs((m) => ({ ...m, [c]: { error: e.response?.data?.error || 'Could not build message' } }));
      }
    });
    return () => { cancelled = true; };
  }, [templateId, classIds, date, user]);

  const markSent = useCallback((c, v) => {
    setSent((s) => {
      const next = { ...s, [c]: v };
      localStorage.setItem(storeKey(date), JSON.stringify(next));
      return next;
    });
  }, [date]);

  const nextId = useMemo(() => classIds.find((c) => !sent[c] && msgs[c]?.message), [classIds, sent, msgs]);
  const doneCount = classIds.filter((c) => sent[c]).length;

  const send = (c) => {
    const m = msgs[c];
    if (!m?.message) return;
    markSent(c, true);
    if (m.groupLink) {
      // Group invite links can't carry text: copy it, open the group, admin pastes.
      copyText(m.message);
      setNote(`Report for ${classLabel(c)} copied. Paste it in the group chat.`);
      window.open(m.groupLink, '_blank', 'noopener');
    } else {
      setNote(`No group link saved for ${classLabel(c)}. Pick the chat in WhatsApp.`);
      window.open(`https://wa.me/?text=${encodeURIComponent(m.message)}`, '_blank', 'noopener');
    }
  };

  const custom = templates ? templates.filter((t) => !t.system) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f3f4f6', zIndex: 9000, overflowY: 'auto' }}>
      <style>{'@keyframes qmPulse{0%,100%{opacity:1}50%{opacity:.45}}'}</style>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '12px 12px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <button onClick={onBack} aria-label="Back to attendance"
            style={{ minWidth: TAP, height: TAP, border: 'none', background: '#e5e7eb', borderRadius: 12, fontSize: 18, cursor: 'pointer' }}>←</button>
          <h2 style={{ margin: 0, fontSize: 19, flex: 1 }}>🚀 WhatsApp Dispatcher</h2>
          <button onClick={onClose} aria-label="Close"
            style={{ width: TAP, height: TAP, border: 'none', background: '#e5e7eb', borderRadius: 12, fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ background: '#dcfce7', borderRadius: 12, padding: '10px 14px', fontSize: 14, marginBottom: 12 }}>
          ✅ Attendance saved for {date}. Sent {doneCount}/{classIds.length}
        </div>

        {/* Template chosen ONCE for every class */}
        {templates === null
          ? <div style={{ height: TAP, borderRadius: 10, background: '#e5e7eb', animation: 'qmPulse 1.2s infinite', marginBottom: 12 }} />
          : <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} aria-label="Message template"
              style={{ width: '100%', minHeight: TAP, fontSize: 16, padding: '0 10px', borderRadius: 10, border: '1.5px solid #d1d5db', marginBottom: 12, background: '#fff' }}>
              <optgroup label="System templates">
                {templates.filter((t) => t.system).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </optgroup>
              {custom.length > 0 && (
                <optgroup label="My custom templates">
                  {custom.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </optgroup>
              )}
            </select>}

        {classIds.map((c) => {
          const m = msgs[c];
          const isNext = c === nextId;
          return (
            <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 64, padding: '8px 12px', marginBottom: 8, borderRadius: 14, background: '#fff', border: isNext ? '2px solid #25d366' : '1px solid #e5e7eb', boxShadow: isNext ? '0 0 0 4px rgba(37,211,102,.15)' : 'none' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{classLabel(c)}</div>
                {m?.error && <div style={{ fontSize: 12, color: '#b91c1c' }}>{m.error}</div>}
                {isNext && <div style={{ fontSize: 12, color: '#15803d' }}>Next up</div>}
              </div>
              {sent[c] ? (
                <>
                  <span style={{ color: '#15803d', fontWeight: 800, fontSize: 15 }}>✅ Sent</span>
                  <button onClick={() => markSent(c, false)} aria-label={`Mark ${classLabel(c)} as not sent`}
                    style={{ minWidth: TAP, minHeight: TAP, border: 'none', background: '#f3f4f6', borderRadius: 10, cursor: 'pointer' }}>↺</button>
                </>
              ) : !m ? (
                <div style={{ width: 130, height: TAP, borderRadius: 10, background: '#e5e7eb', animation: 'qmPulse 1.2s infinite' }} />
              ) : (
                <button onClick={() => send(c)} disabled={!m.message}
                  style={{ minWidth: 130, minHeight: TAP, padding: '0 14px', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 15, color: '#fff', cursor: 'pointer', background: m.message ? (isNext ? '#25d366' : '#16a34a') : '#9ca3af' }}>
                  🚀 Send Report
                </button>
              )}
            </div>
          );
        })}

        {note && <div role="status" style={{ marginTop: 8, fontSize: 13, color: '#166534' }}>{note}</div>}
        {classIds.length > 0 && doneCount === classIds.length && (
          <div style={{ marginTop: 12, textAlign: 'center', fontWeight: 800, color: '#15803d' }}>🎉 All reports sent!</div>
        )}
      </div>
    </div>
  );
}